export const ROUTES = {
  // Public routes
  PUBLIC: {
    HOME: '/',
    TERMS: '/terms-and-conditions',
    PRIVACY: '/privacy-policy',
    TRANSFORMATION: '/the-shift',
    WHY: '/why-skolarminds',
    INTELLIGENCE: '/intelligence',
    BOOK_DEMO: '/book-demo',
  },

  // Auth routes
  AUTH: {
    LOGIN: '/login',
    SIGNUP: '/signup',
    FORGOT_PASSWORD: '/forgot-password',
    FORGOT_PIN: '/forgot-pin',
    SET_PIN: '/set-pin',
    EMAIL_NOT_VERIFIED: '/email-not-verified',
    VERIFY_EMAIL: '/verify-email',
    SELECT_PROFILE: '/select-profile',
    VERIFY_PHONE: '/verify-phone',
    SET_PASSWORD: '/set-password',
    SUBSCRIBE: '/subscribe',
  },

  // Dashboard routes
  DASHBOARD: {
    ROOT: '/dashboard',
    INSTITUTE: '/instituteInfo',
    SETTINGS: {
      BOARD_TYPES: '/institute/settings/board-types',
      FINANCIAL_YEARS: '/institute/settings/financial-years',
      DOCUMENT_LOGOS: '/institute/settings/document-logos',
      DIGITAL_SIGNATURES: '/institute/settings/digital-signatures',
      DOCUMENT_SIGNATURES: '/institute/settings/document-signatures',
    },
  },

  // Approvals routes
  APPROVALS: {
    PENDING: '/approvals/pending',
    STUDENTS: '/approvals/students',
  },

  // Academic routes
  ACADEMIC: {
    CLASSES: {
      LIST: '/classes',
      NEW: '/classes/new',
      EDIT: '/classes/:id/edit',
    },
    SUBJECTS: {
      LIST: '/subjects',
      NEW: '/subjects/new',
      ASSIGN_TEACHER: '/subjects/assign-teacher',
      EDIT_ASSIGNMENT: '/subjects/:id/edit-assignment',
      MANAGE_CLASS_SUBJECTS: '/subjects/manage-class-subjects',
    },
    GRADING: {
      LIST: '/gradingSystems',
      VIEW: '/gradingSystems/view/:id',
      CREATE: '/gradingSystems/create',
      EDIT: '/gradingSystems/edit/:id',
    },
    EVENTS: {
      LIST: '/events',
      NEW: '/events/new',
      EDIT: '/events/:id/edit',
      DETAIL: '/events/:eventId',
      MY_EVENTS: '/my-events',
      MY_EVENT_DETAIL: '/my-events/:eventId',
    },
    ATTENDANCE: {
      EMPLOYEE: '/attendance/employee',
      STUDENT: '/attendance/student',
      REPORT: '/attendance/report',
    }
  },

  // Employee routes
  EMPLOYEE: {
    DASHBOARD: '/employee/dashboard',
    PROFILE: '/employee/profile',
    MY_DETAILS: '/my-details',
    ATTENDANCE: {
      CHECKIN: '/employee/check-in',
      HISTORY: '/employee/attendance-history',
    }
  },

  // Finance routes
  FINANCE: {
    FEE: {
      STRUCTURE: '/fees/structure',
      COLLECT: '/fees/collectfees',
      MY_FEES: '/my-fees',
    },
    SALARY: {
      COMPONENTS: '/salarycomponents',
      COMPUTE: '/salary/compute',
    },
  },

  // Staff routes
  STAFF: {
    EMPLOYEES: {
      LIST: '/employees',
      DIRECTORY: '/employees/directory',
      NEW: '/employees/new',
      SUMMARY: '/employees/:id',
      EDIT: '/employees/:id/edit',
    },
  },

  // Student routes
  STUDENT: {
    LIST: '/students',
    NEW: '/students/new',
    SUMMARY: '/students/:id',
    EDIT: '/students/:id/edit',
  },

  // Certificate routes
  CERTIFICATES: {
    GENERATE: '/certificates/generate',
    TRANSFER_OVERRIDES: '/certificates/transfer-overrides',
  },

  // Schedule routes
  SCHEDULE: {
    TIMETABLE: {
      // Admin routes
      DASHBOARD: '/timetables',
      VIEW_TIMETABLES: '/timetables/view', // View timetables by teacher or class/section
      SCHEDULE_VIEWER: '/timetables/schedule-viewer', // Magazine-style schedule viewer
      WEEK_EDITOR: '/timetables/section/:sectionId/edit',
      AUTO_GENERATE: '/timetables/generate',
      TIME_SLOTS: '/timetables/time-slots',
      SCHEDULE_PROFILES: '/timetables/schedule-profiles', // Schedule Groups/Profiles Management
      BATCH_UPDATE: '/timetables/batch-update',
      CLASS_TIME_SLOTS: '/timetables/class-time-slots', // Class-specific time slot override
      
      // Advanced features (hidden from main navigation, accessible via dashboard actions)
      ENHANCED_DASHBOARD: '/timetables/enhanced', // Enhanced dashboard with analytics
      MASTER_GRID: '/timetables/master-grid', // Master grid view for bulk operations
      KANBAN_EDITOR: '/timetables/section/:sectionId/kanban', // Kanban-style drag-drop editor
      
      // Teacher routes
      MY_SCHEDULE: '/mySchedule',
      MY_AVAILABILITY: '/myAvailability',
      
      // Student routes
      MY_TIMETABLE: '/myTimetable',
    },
  },

  // Exam routes
  EXAM: {
    // Admin routes - distinct path
    MANAGE: '/exams/manage',
    LIST: '/exams/manage', // Alias for backward compatibility
    CREATE: '/exams/create',
    EDIT: '/exams/edit/:id',
    SCHEDULES: {
      LIST: '/exams/:examId/schedules',
      EDIT: '/exams/:examId/class/:classId/section/:sectionId/bulk-edit'
    },
    ENTER_SCORES: '/exams/enter-scores',
    MARKSENTRY: '/exams/:examId/schedules/:scheduleId/marks',
    ATTENDANCE: '/exams/:examId/schedules/:scheduleId/attendance',
    
    // Teacher routes - distinct path
    MY_EXAMS: '/exams/my-exams',
    TODOS: '/exams/todos',
    
    // Student routes - distinct paths
    RESULTS: '/exams/results',
    VIEW_RESULTS: '/exams/results/:examId',
    STUDENT_SCHEDULES: '/exams/my-schedule'
  },

  // Reports routes
  REPORTS: {
    EXAM_SCORES: '/reports/exam-scores',
  },

  // Syllabus routes
  SYLLABUS: {
    LIST: '/syllabus',
    NEW: '/syllabus/new',
    EDIT: '/syllabus/:id/edit',
    DETAIL: '/syllabus/:id',
    STUDENT_VIEW: '/my-syllabus',
  },

  // Leave routes
  LEAVE: {
    LIST: '/leaves',
    APPLY: '/leaves/apply',
    APPROVALS: '/leaves/approvals',
    STUDENT_REQUESTS: '/leaves/student-requests',
    EMPLOYEE_LEAVE_POLICY: '/leave-policy'
  },

  // Calendar routes
  CALENDAR: {
    HOLIDAYS: '/holidays',
  },

  // User routes
  USER: {
    PROFILE: '/profile',
    FORGOT_PASSWORD: '/forgot-password',
    RESET_PASSWORD: '/reset-password',
  },

  // Expenses routes
  EXPENSES: {
    CATEGORIES: {
      LIST: '/expenses/categories',
    },
    CLAIMS: {
      MY: '/expenses/claims/my',
      SUBMIT: '/expenses/claims/submit',
      DETAIL: '/expenses/claims/:id',
    },
    APPROVALS: {
      PENDING: '/expenses/approvals/pending',
    },
    SETTLEMENTS: {
      LIST: '/expenses/settlements',
    },
  },

  // Error routes
  ERROR: {
    UNAUTHORIZED: '/unauthorized',
    NOT_FOUND: '*',
  },
  TRANSPORT: {
    VEHICLES: {
      LIST: '/transport/vehicles',
      NEW: '/transport/vehicles/new',
      EDIT: '/transport/vehicles/:id/edit',
    },
    ROUTES: {
      LIST: '/transport/routes',
      NEW: '/transport/routes/new',
      EDIT: '/transport/routes/:id/edit',
      STUDENTS: '/transport/routes/:routeId/students',
    },
    DRIVERS: {
      LIST: '/transport/drivers',
      NEW: '/transport/drivers/new',
      EDIT: '/transport/drivers/:id/edit',
    },
    ENROLLMENT: {
      LIST: '/transport/enrollment',
    },
    ATTENDANCE: {
      LIST: '/transport/attendance',
    },
    LOGS: '/transport/logs',
  },
  HOSTEL: {
    LIST: '/hostel',
    NEW: '/hostel/new',
    EDIT: '/hostel/:hostelId/edit',
    VIEW: '/hostel/:hostelId',
    ROOMS: {
      NEW: '/hostel/:hostelId/rooms/new',
      EDIT: '/hostel/:hostelId/rooms/:roomId/edit',
      VIEW: '/hostel/:hostelId/rooms/:roomId'
    },
    RESIDENTS: {
      LIST: '/hostel/residents'
    },
    ATTENDANCE: {
      LIST: '/hostel/attendance'
    }
  },
  SETTINGS: {
    ROOT: '/settings',
    ACADEMIC_YEARS: {
      LIST: '/settings/academic-years',
      NEW: '/settings/academic-years/new',
      EDIT: '/settings/academic-years/:id/edit',
    },
  },

  // Communication routes
  COMMUNICATION: {
    DASHBOARD: '/communication',
    HISTORY: '/communication/history', // Optional: view-only history page
    MESSAGES: '/communication/messages',
    FEE_REMINDERS: '/communication/fee-reminders',
  },

  // Notifications
  NOTIFICATIONS: {
    LIST: '/notifications',
  },

  // Promotion routes
  PROMOTION: {
    EXECUTE: '/promotion/execute',
    SECTION_ASSIGNMENT: '/promotion/section-assignment',
    SECTION_TRANSFER: '/promotion/section-transfer',
  },

  // User Management routes
  USER_MANAGEMENT: {
    USERS: '/user-management/users',
    ROLES: '/user-management/roles',
  },

  // Assignment routes
  ASSIGNMENT: {
    LIST: '/assignments',
    CREATE: '/assignments/create',
    VIEW: '/assignments/:id/view',
    EDIT: '/assignments/:id/edit',
    SUBMISSIONS: '/assignments/:id/submissions',
    STUDENT_LIST: '/my-assignments',
    ATTEMPT: '/my-assignments/:id/attempt',
    RESULT: '/my-assignments/:id/result',
  },

  ONLINE_CLASSES: {
    TEACHER: {
      ROOT: '/online-classes',
      DASHBOARD: '/online-classes/dashboard',
      SCHEDULE: '/online-classes/schedule',
      RECORDINGS: '/online-classes/recordings',
    },
    STUDENT: {
      ROOT: '/my-online-classes',
      DASHBOARD: '/my-online-classes',
      RECORDINGS: '/my-online-classes/recordings',
    },
  },
  
  HOMEWORK: {
    ROOT: '/homework',
    EVALUATE: '/homework/:homeworkId/evaluate',
  },
} as const;
